import { Container, Sprite, Texture } from "pixi.js";

const TOP_COLOR = { r: 0x11, g: 0x11, b: 0x20 };
const BOTTOM_COLOR = { r: 0x0e, g: 0x0e, b: 0x1a };

interface BackgroundProps {
  width: number;
  height: number;
}

function applyGradient(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
): void {
  const gradient = ctx.createLinearGradient(0, 0, 0, height);
  gradient.addColorStop(0, `rgb(${TOP_COLOR.r}, ${TOP_COLOR.g}, ${TOP_COLOR.b})`);
  gradient.addColorStop(1, `rgb(${BOTTOM_COLOR.r}, ${BOTTOM_COLOR.g}, ${BOTTOM_COLOR.b})`);
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, width, height);
}

function ditherRgb(ctx: CanvasRenderingContext2D, width: number, height: number): void {
  const image = ctx.getImageData(0, 0, width, height);
  const data = image.data;

  for (let i = 0; i < data.length; i += 4) {
    const noise = Math.random() - 0.5;
    const amplitude = noise * 2;

    for (let c = 0; c < 3; c++) {
      const value = data[i + c] + amplitude;

      if (value < 0) {
        data[i + c] = 0;
        continue;
      }

      if (value > 255) {
        data[i + c] = 255;
        continue;
      }

      data[i + c] = value;
    }
  }

  ctx.putImageData(image, 0, 0);
}

function createBackgroundTexture(width: number, height: number): Texture {
  const resolution = window.devicePixelRatio || 1;
  const canvas = document.createElement("canvas");
  const pixelWidth = Math.ceil(width * resolution);
  const pixelHeight = Math.ceil(height * resolution);
  canvas.width = pixelWidth;
  canvas.height = pixelHeight;
  const ctx = canvas.getContext("2d")!;
  ctx.scale(resolution, resolution);

  applyGradient(ctx, width, height);
  ditherRgb(ctx, pixelWidth, pixelHeight);

  return Texture.from({ resource: canvas, resolution });
}

export class Background {
  private sprite: Sprite;
  private currentTexture?: Texture;
  private previousWidth = 0;
  private previousHeight = 0;

  constructor(stage: Container) {
    this.sprite = new Sprite();
    stage.addChild(this.sprite);
  }

  draw(props: BackgroundProps): void {
    const { width, height } = props;

    if (width <= 0 || height <= 0) {
      return;
    }

    if (width === this.previousWidth && height === this.previousHeight) {
      return;
    }

    this.previousWidth = width;
    this.previousHeight = height;
    this.currentTexture?.destroy(true);
    this.currentTexture = createBackgroundTexture(width, height);
    this.sprite.texture = this.currentTexture;
    this.sprite.width = width;
    this.sprite.height = height;
  }

  destroy(): void {
    this.currentTexture?.destroy(true);
    this.sprite.destroy();
  }
}
